import axios from "axios";
import i18n from "@/i18n";

export const api = axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    withCredentials: true,
});

/** Look up an i18n translation for a raw error key. Falls back to the key
 * itself if no `errors.<key>` translation exists, so we never surface the
 * empty string. */
function trErr(key: string): string {
    try {
        const k = `errors.${key}`;
        const translated = i18n.t(k);
        // i18next returns the key itself when not found; treat that as "no translation".
        if (translated && translated !== k) return String(translated);
    } catch { /* noop */ }
    return key;
}

// Attempt one silent refresh on 401 before failing.
let refreshing: Promise<void> | null = null;

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error.config;
        // Global handler: if backend gate blocks with mfa_setup_required, redirect to setup page.
        const detail = error.response?.data?.detail;
        // Fresh 503 from DB unavailability shield — surface a friendly toast once per axios cycle.
        if (error.response?.status === 503 && error.response?.data?.error === "service_unavailable_db") {
            if (typeof window !== "undefined") {
                // Lazy import to avoid a hard dependency on sonner in this module's public surface.
                import("sonner").then(({ toast }) => {
                    toast.error(error.response?.data?.message || "Sistem müvəqqəti bərpa olunur, 1 dəqiqədən yenidən yoxlayın.");
                }).catch(() => {});
            }
            return Promise.reject(error);
        }
        if (
            error.response?.status === 403 &&
            detail && typeof detail === "object" && detail.error === "mfa_setup_required" &&
            typeof window !== "undefined" &&
            !window.location.pathname.startsWith("/settings/security") &&
            !window.location.pathname.startsWith("/login")
        ) {
            window.location.replace("/settings/security");
        }
        if (
            error.response?.status === 401 &&
            !original?._retry &&
            !original?.url?.includes("/auth/")
        ) {
            original._retry = true;
            if (!refreshing) {
                refreshing = api
                    .post("/auth/refresh")
                    .then(() => undefined)
                    .catch(() => undefined)
                    .finally(() => {
                        refreshing = null;
                    });
            }
            await refreshing;
            return api(original);
        }
        return Promise.reject(error);
    }
);

export function formatApiError(err: any): string {
    // 503 service_unavailable_db → friendly i18n toast; caller receives a short key.
    if (err?.response?.status === 503 && err?.response?.data?.error === "service_unavailable_db") {
        return trErr("service_unavailable_db");
    }
    // Any other 5xx → generic friendly, TRANSLATED key (never surface the raw
    // sentinel "server_error_generic" in a panel).
    if (err?.response?.status && err.response.status >= 500 && err.response.status < 600) {
        return trErr("server_error_generic");
    }
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message ?? trErr("unexpected_error");
    if (typeof detail === "string") return trErr(detail);
    if (Array.isArray(detail))
        return detail
            .map((e: any) => (e?.msg ? String(e.msg) : JSON.stringify(e)))
            .filter(Boolean)
            .join(" · ");
    if (typeof detail === "object" && detail.error) return trErr(String(detail.error));
    return JSON.stringify(detail);
}
